import { useAuth } from 'hooks/useAuth';
import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import LeftNavigation from 'components/Navigation/LeftNavigation';
import OnboardingHeader from 'components/Header/OnboardingHeader';
import useIsMobile from 'hooks/useIsMobile';
import { http } from 'middleware/apiMiddleware';
import { getUser } from 'constants/apiUrls';

const RootLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authContext = useAuth();
  const { pathname } = useLocation();
  const isMobile = useIsMobile();

  const isOnboardingOrSignupPage =
    pathname.includes('/onboarding-business') || pathname.includes('/sign-up');

  // Pages that need fixed viewport height (no whole page scroll) - DESKTOP ONLY
  const isFixedHeightPage = pathname.includes('/business-details') && !isMobile;

  // This is used as react router is keeping scroll position persisted while we navigate between the routes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Fetch full user profile on app load to ensure phone fields are available
  useEffect(() => {
    if (authContext?.user?.uuid) {
      const fetchUserProfile = async () => {
        try {
          const { data } = await http.get(getUser(authContext.user!.uuid));
          if (data) {
            const raw = data as Record<string, any>;
            const mapped: Record<string, any> = {};
            mapped.phoneNumber = raw.phoneNumber || raw.phone_number || '';
            mapped.phoneCountryCode = raw.phoneCountryCode || raw.phone_country_code || '';
            mapped.phoneCountryExtension = raw.phoneCountryExtension || raw.phone_country_extension || '';
            mapped.phoneCountryName = raw.phoneCountryName || raw.phone_country_name || '';
            mapped.profilePicture = raw.profilePicture || raw.profile_picture || '';
            if (mapped.profilePicture) {
              const separator = mapped.profilePicture.includes('?') ? '&' : '?';
              mapped.profilePicture = `${mapped.profilePicture}${separator}t=${Date.now()}`;
            }
            if (raw.name) mapped.name = raw.name;
            authContext.updateUser?.(mapped);
          }
        } catch {
          // Silently fail
        }
      };
      fetchUserProfile();
    }
  }, [authContext?.user?.uuid]);

  // Prevent body scroll on fixed height pages (desktop only)
  useEffect(() => {
    if (isFixedHeightPage) {
      document.documentElement.classList.add('no-scroll');
      window.scrollTo(0, 0);
    } else {
      document.documentElement.classList.remove('no-scroll');
    }
    return () => {
      document.documentElement.classList.remove('no-scroll');
    };
  }, [isFixedHeightPage]);

  return (
    <div
      className={`bg-offWhite ${isFixedHeightPage ? 'h-screen overflow-hidden' : 'min-h-screen overflow-x-hidden'}`}
    >
      <Toaster />
      {!!authContext?.user ? (
        <>
          {isOnboardingOrSignupPage ? (
            // Onboarding/Signup Flow - Show only header, no left navigation
            <div className="min-h-screen bg-white">
              <OnboardingHeader />
              <div className="pt-[60px] md:pt-[88px] bg-white">{children}</div>
            </div>
          ) : (
            // Regular authenticated pages - Show full left navigation
            <div
              className={`${isMobile ? 'flex flex-col' : 'flex'} ${isFixedHeightPage ? 'h-full overflow-hidden' : 'min-h-screen'} relative`}
            >
              {/* Left Navigation Sidebar / Mobile Header */}
              <LeftNavigation />

              {/* Main Content Area */}
              <div
                className={`flex-1 ${isFixedHeightPage ? 'h-full overflow-hidden' : ''} ${isMobile ? 'pt-[60px] bg-white' : ''}`}
              >
                {children}
              </div>

              {/* Horizontal line under nav header — above main content paint so it is not covered by sticky headers */}
              {!isMobile && (
                <div className="pointer-events-none absolute left-0 right-0 top-[88px] z-[35] h-px bg-borderGrey" />
              )}
            </div>
          )}
        </>
      ) : (
        <div className="min-h-screen">{children}</div>
      )}
      <div id="portal" />
      <div id="banner" />
    </div>
  );
};

export default RootLayout;
